import { Component, EventEmitter, forwardRef, Input, OnDestroy, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { iValidatedControl } from '../iValidatedControl';
import { BaseCustomControl } from '../baseCustomControl';
import { HostBinding } from '@angular/core';
import { ElementRef, ViewChild } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { ListItem } from '../../model/listItem';

export enum DDLModes {
  Default = "Default",
  States = "States",
  Countries = "Countries",
  CCProcessors = "CCProcessors",
  ACHProcessors = "ACHProcessors",
  Gateways = "Gateways",
  Brands = "Brands",
  TimeZones = "TimeZones",
  BusinessTypes = "BusinessTypes",
  UnitsOfMeasure = "UnitsOfMeasure",
  TaxTypes = "TaxTypes",
  ProcessingTypes = "ProcessingTypes"
}

@Component({
  selector: 'app-selectbox',
  standalone: false,
  templateUrl: './selectbox.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Selectbox),
      multi: true,
    },
  ],
})
export class Selectbox extends BaseCustomControl implements ControlValueAccessor, iValidatedControl, OnDestroy {

  @HostBinding('class.formField') formFieldClass = true; // Always applied
  @HostBinding('class.readOnly') get isReadOnly() {
    return this.readonly;
  }


  @Input() align:string = "";
  @Input() options:ListItem[] = [];
  @Input() validationLocation:string = "right"; // right | under
  @Input() type:DDLModes = DDLModes.Default;
  @Input() showRequired:boolean = false;
  @Input() L2L3Required:boolean = false;
  @Input() multiple:boolean = false;
  @Input() searchable: boolean = false;
  @Input() placeholder: string = "";

  @Output() searchChange = new EventEmitter<string>();

  @ViewChild('container') container: ElementRef<HTMLElement> | undefined;
  @ViewChild('searchInput') searchInput: ElementRef<HTMLInputElement> | undefined;

  // State for the searchable (custom) dropdown
  dropdownOpen = false;
  searchText = '';
  activeIndex = -1;
  private selectedOptionName = '';

  private readonly onDocumentPointerDown = (e: Event) => {
    const root = this.container?.nativeElement;
    if (root && !root.contains(e.target as Node)) {
      this.closeDropdown();
      this.cdr.markForCheck();
    }
  };

  constructor(private cdr: ChangeDetectorRef) {
    super(cdr);
  }

  ngOnDestroy(): void {
    document.removeEventListener('mousedown', this.onDocumentPointerDown, true);
    document.removeEventListener('touchstart', this.onDocumentPointerDown, true);
  }

  /** Display text for the current value in the searchable dropdown. */
  get selectedName(): string {
    if (this.boundValue === undefined || this.boundValue === null || this.boundValue === '') {
      return '';
    }
    const match = this.options?.find(o => o.id === this.boundValue);
    // Options may be replaced by a search, so fall back to the last picked name.
    return match ? match.name : this.selectedOptionName;
  }

  toggleDropdown() {
    this.dropdownOpen ? this.closeDropdown() : this.openDropdown();
  }

  openDropdown() {
    if (this.disabled || this.dropdownOpen) {
      return;
    }
    this.dropdownOpen = true;
    this.searchText = '';
    const selectedIdx = this.options?.findIndex(o => o.id === this.boundValue) ?? -1;
    this.activeIndex = selectedIdx;
    // Reset any filter left over from a previous search.
    this.txtSearch_keyup('');
    // Add listeners only while open to avoid a document listener per control.
    document.addEventListener('mousedown', this.onDocumentPointerDown, true);
    document.addEventListener('touchstart', this.onDocumentPointerDown, true);
    // Focus the search box once it has rendered.
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
      this.cdr.markForCheck();
    });
  }

  closeDropdown(refocusTrigger = false) {
    if (!this.dropdownOpen) {
      return;
    }
    this.dropdownOpen = false;
    document.removeEventListener('mousedown', this.onDocumentPointerDown, true);
    document.removeEventListener('touchstart', this.onDocumentPointerDown, true);
    this.validate();
    if (refocusTrigger) {
      (this.container?.nativeElement.querySelector('.searchable-select__trigger') as HTMLElement | null)?.focus();
    }
  }

  onSearchInput(value: string) {
    this.searchText = value;
    this.activeIndex = 0;
    this.txtSearch_keyup(value);
  }

  selectOption(item: ListItem) {
    this.boundValue = item.id;
    this.selectedOptionName = item.name;
    if (this.validateOn == "change") {
      this.validate();
    }
    this.onChange(this.boundValue);
    this.onChanged.emit(this.boundValue);
    this.closeDropdown(true);
  }

  onDropdownKeydown(e: KeyboardEvent) {
    const count = this.options?.length ?? 0;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!this.dropdownOpen) {
          this.openDropdown();
        } else if (count) {
          this.activeIndex = (this.activeIndex + 1) % count;
          this.scrollActiveIntoView();
        }
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (this.dropdownOpen && count) {
          this.activeIndex = this.activeIndex <= 0 ? count - 1 : this.activeIndex - 1;
          this.scrollActiveIntoView();
        }
        break;
      case 'Enter':
        if (this.dropdownOpen) {
          e.preventDefault();
          const item = this.options?.[this.activeIndex];
          if (item) {
            this.selectOption(item);
          }
        }
        break;
      case 'Escape':
        if (this.dropdownOpen) {
          e.preventDefault();
          this.closeDropdown(true);
        }
        break;
      case 'Tab':
        this.closeDropdown();
        break;
    }
  }

  private scrollActiveIntoView() {
    setTimeout(() => {
      this.container?.nativeElement
        .querySelector('.searchable-select__option.is-active')
        ?.scrollIntoView({ block: 'nearest' });
    });
  }


  override ngOnInit(): void {
    super.ngOnInit();
  }

  override clear() {
    this.boundValue = "";
    this.actualValue = "";
  }

  txtSearch_keyup(event: string) {
    this.searchChange.emit(event);
  }


  private setInitialValue() {
    if (this.boundValue && this.options) {
      // Ensure the option exists in the dropdown
      const exists = this.options.some(opt => opt.id === this.boundValue);
      if (!exists) {
        // If the bound value isn't in options, clear it
        this.boundValue = undefined;
      }
    }
  }

  ngAfterViewInit() {
    if (this.readonly) {
      return;
    }

    // Don't use document.getElementById(this.myId): the template has two
    // <select>s sharing that id, and getElementById can miss the view at
    // AfterViewInit. Query the visible select from the local template ref.
    const select = this.getSelectElement();
    select?.setAttribute('data-selected-index', '-1');
  }

  private getSelectElement(): HTMLSelectElement | null {
    const root = this.container?.nativeElement;
    if (!root) {
      return null;
    }

    const selects = Array.from(root.querySelectorAll('select'));
    return (
      selects.find((el) => !el.hidden) ??
      selects[0] ??
      null
    );
  }

  get value():string {
    if (this.boundValue && !this.multiple) {
      return this.boundValue.trim();
    } else if(this.boundValue && this.multiple) {
      return this.boundValue.join(",");
    } else {
      return "";
    }
  }

  public lookupOptions(key:string) {
    // if (this.type == DDLModes.States) {
    //   this.cache.getStatesList(key)
    //   .then(s => {
    //     this.options = s;
    //   })
    // }
  }

  
  public clearSelection() {
    this.boundValue = undefined;
  }

  private _computeIsValid(): boolean {
    if (this.required && this.value == "") {
      return false;
    }
    return true;
  }
  
  // Read-only check — safe to call on every keystroke/change, no UI side effects
  public checkValiditySilently(): boolean {
    return this._computeIsValid();
  }
  
  public validate(): boolean {
    this.isValid = this._computeIsValid();
    this.validationMsg = this.isValid ? "" : "required";
  
    if (!this.isValid) {
      this._handle_validateFailed();
    }
    else {
      this._handle_validatePassed();
    }
  
    return this.isValid;
  }

  _handle_change(e:Event) {

   
    const c = e.target as HTMLSelectElement;
    c.setAttribute('data-selected-index', c.selectedIndex.toString());

    if (this.validateOn == "change") {
      this.validate();
    }

    this.onChange(this.boundValue);

    this.onChanged.emit(this.boundValue);

    return true;
   }

  _handle_onBlur(e:FocusEvent) {

    this.validate();
  }
}
